import { useEffect } from 'react';

import { resolveCommunityForSite } from '@flexo/general';
import { METADATA } from '@flexo/reducers';
import i18next from 'i18next';
import * as luxon from 'luxon';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

function getQueryDateFrom(search: string) {
  const queryDate = new URLSearchParams(search || '').get('dateFrom');
  return queryDate || luxon.DateTime.now().toFormat('yyyy-MM-dd');
}

function getEndUserMemberSites(member: any, entities: any) {
  const allSites = entities?.site || entities?.sites || [];
  const memberSiteIds = new Set(
    [
      ...(((member?.sites as any[]) || []).map((site: any) => site?.siteId)),
      ...(((member?.changelog as any[]) || []).map(
        (entry: any) => entry?.siteId
      )),
    ].filter(Boolean)
  );
  const memberSites = [
    ...allSites.filter((site: any) =>
      memberSiteIds.size > 0
        ? memberSiteIds.has(site?.siteId)
        : site?.memberId === member?.memberId
    ),
    ...(((member?.sites as any[]) || []).filter((site: any) => site?.siteId)),
  ];

  return Array.from(
    new Map(
      memberSites
        .filter((site: any) => site?.siteId)
        .map((site: any) => [site.siteId, site])
    ).values()
  );
}

function getUniqueMembers(metadata: any) {
  const entities = metadata?.entities as any;
  return [
    ...((entities?.member || entities?.members || []) as any[]),
    ...((metadata?.member?.current || []) as any[]),
    ...((metadata?.member?.previous || []) as any[]),
    ...((metadata?.member?.future || []) as any[]),
  ].reduce((acc: any[], member: any) => {
    if (
      member?.memberId &&
      !acc.some((item: any) => item?.memberId === member.memberId)
    ) {
      acc.push(member);
    }
    return acc;
  }, []);
}

/**
 * FLEXO-6066 / end-user web: ad-hoc replacement for `CommunityResolver`.
 *
 * The admin `CommunityResolver` calls `GET /community/catalog/v1_4/community/{id}`,
 * which is admin-only and returns 403 (ERR_AUTH_003) for the `org:end_user` role.
 * Mirrors `EndUserMemberResolver`: read the community from the already-authorized
 * `entities` response (loaded by `EntitiesResolver`) and set it as the selected
 * entity — no extra HTTP call, no 403.
 */
export const EndUserCommunityResolver = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const metadata = useSelector((state: any) => state.metadata);
  const user = useSelector((state: any) => state.user);

  function endUserCommunityInit() {
    const entities = metadata?.entities as any;
    const communities =
      entities?.community ||
      entities?.communities ||
      [];

    let _community: any = null;
    const pathSegment = location.pathname.split('/')[1];
    const memberId =
      user?.metadata?.flexo_user_id || user?.tokenContent?.flexo_user_id;
    const member = getUniqueMembers(metadata).find(
      (item: any) => item?.memberId === memberId
    );
    const allSites = entities?.site || entities?.sites || [];
    const hasRoutingData = !!member || allSites.length > 0;
    const memberSites = member
      ? getEndUserMemberSites(member, entities)
      : allSites;
    const dateFrom = getQueryDateFrom(location.search);
    const siteCommunityIds = Array.from(
      new Set(
        memberSites
          .map(
            (site: any) =>
              resolveCommunityForSite(site, dateFrom) || site?.communityId
          )
          .filter(Boolean)
      )
    );

    if (pathSegment === 'communities' && hasRoutingData) {
      if (memberSites.length === 1 && memberSites[0]?.siteId) {
        navigate(`/site/${memberSites[0].siteId}${location.search}`, {
          replace: true,
        });
        return;
      }

      if (siteCommunityIds.length <= 1 && memberId) {
        navigate(`/member/${memberId}${location.search}`, {
          replace: true,
        });
        return;
      }
    }

    if (pathSegment === 'communities' && communities.length > 0) {
      _community = {
        __allCommunities: true,
        communityId: '__all_communities__',
        communityName:
          i18next.t('dropdowns.allCommunities') || 'All communities',
        communityExternalId: '',
        communities,
      };
    } else if (!params.id && communities.length > 0) {
      _community = communities[0];
    } else if (params.id) {
      _community = communities.find(
        (c: any) => c.communityId === params.id
      );
    }

    if (!_community) {
      return;
    }

    dispatch(
      METADATA.SetSelectedEntity({
        selectedEntity: _community,
        selectedEntityId: _community?.communityId,
        entityType: 'community',
      } as any)
    );
  }

  useEffect(() => {
    endUserCommunityInit();
  }, [
    params.id,
    location.pathname,
    location.search,
    metadata?.entities,
    metadata?.member,
    user?.metadata?.flexo_user_id,
    user?.tokenContent?.flexo_user_id,
  ]);

  return <></>;
};

export default EndUserCommunityResolver;
